// Досье (RD-149 r3): нормализация ответов RD-161 — дисциплина, биржа, кадровые документы.
// Все операции по User UUID. Даты/время — naive local подразделения (YYYY-MM-DDTHH:MM:SS):
// время берётся из строки как есть, без перевода в UTC и без фиксированного смещения.
// Отсутствующее/невалидное значение — null («—»), не 0 и не mock.
import dayjs, { type Dayjs } from 'dayjs'
import { DATE_KEY } from './dossierModel'

const intOrNull = (value: unknown) => (typeof value === 'number' && Number.isInteger(value) ? value : null)
const numberOrNull = (value: unknown) => {
    if (value === null || value === undefined || value === '') return null
    const numeric = Number(value)
    return Number.isFinite(numeric) ? numeric : null
}
const textOrNull = (value: unknown) => (typeof value === 'string' && value ? value : null)
const objectOf = (value: unknown) => (value && typeof value === 'object' ? value as Record<string, unknown> : {})
const listOf = (value: unknown) => (Array.isArray(value) ? value.filter(item => item && typeof item === 'object') as Record<string, unknown>[] : [])

// «HH:mm» из naive datetime без пересчёта часового пояса.
export const timeOf = (value: string | null) => (value && value.length >= 16 ? value.slice(11, 16) : null)
export const timeRange = (start: string | null, end: string | null) => {
    const from = timeOf(start)
    const to = timeOf(end)
    return from || to ? `${from ?? '—'}–${to ?? '—'}` : null
}

export interface Paged<T> {
    count: number | null
    results: T[]
}

// ---------- Дисциплина: GET /api/user/{id}/discipline/ ----------

export type DisciplineKind = 'violation' | 'no_marks' | 'absence'

export interface DisciplineTotals {
    violationsCount: number | null
    lateCount: number | null
    lateMinutes: number | null
    earlyCount: number | null
    earlyMinutes: number | null
    noMarksDays: number | null
    absenceDays: number | null
    manualMarksCount: number | null
    seriousCount: number | null
}

export interface DisciplineEvent {
    shiftId: string
    employmentId: string | null
    departmentName: string | null
    date: string
    kind: DisciplineKind
    lateMinutes: number | null
    earlyMinutes: number | null
    serious: boolean
    planStart: string | null
    planEnd: string | null
    factStart: string | null
    factEnd: string | null
    // Часы decimal (shift.duration, с обедом); null для absence/no_marks.
    duration: number | null
    note: string | null
    manualAuthor: string | null
}

export interface DisciplinePage extends Paged<DisciplineEvent> {
    totals: DisciplineTotals
}

const KINDS: DisciplineKind[] = ['violation', 'no_marks', 'absence']

export function normalizeDisciplinePage(body: unknown): DisciplinePage {
    const value = objectOf(body)
    const totals = objectOf(value.totals)
    return {
        totals: {
            violationsCount: intOrNull(totals.violations_count),
            lateCount: intOrNull(totals.late_count),
            lateMinutes: intOrNull(totals.late_minutes),
            earlyCount: intOrNull(totals.early_count),
            earlyMinutes: intOrNull(totals.early_minutes),
            noMarksDays: intOrNull(totals.no_marks_days),
            absenceDays: intOrNull(totals.absence_days),
            manualMarksCount: intOrNull(totals.manual_marks_count),
            seriousCount: intOrNull(totals.serious_count),
        },
        count: intOrNull(value.count),
        results: listOf(value.results)
            .filter(item => typeof item.shift_id === 'string' && typeof item.date === 'string' && KINDS.includes(item.kind as DisciplineKind))
            .map((item) => {
                const author = item.manual_author ? objectOf(item.manual_author) : null
                const authorName = author ? [textOrNull(author.last_name), textOrNull(author.first_name)].filter(Boolean).join(' ') : ''
                return {
                    shiftId: item.shift_id as string,
                    employmentId: textOrNull(item.employment_id),
                    departmentName: textOrNull(objectOf(item.department).name),
                    date: item.date as string,
                    kind: item.kind as DisciplineKind,
                    lateMinutes: intOrNull(item.late_minutes),
                    earlyMinutes: intOrNull(item.early_minutes),
                    serious: item.serious === true,
                    planStart: textOrNull(item.plan_work_start_at),
                    planEnd: textOrNull(item.plan_work_end_at),
                    factStart: textOrNull(item.fact_work_start_at),
                    factEnd: textOrNull(item.fact_work_end_at),
                    duration: numberOrNull(item.duration),
                    note: textOrNull(item.note),
                    manualAuthor: authorName || null,
                }
            }),
    }
}

// Подписи строки: у violation опоздание и ранний уход могут быть в одной строке. Классификация — серверная.
export function disciplineLabels(event: DisciplineEvent): string[] {
    if (event.kind === 'absence') return ['Прогул']
    if (event.kind === 'no_marks') return ['Нет отметки ухода']
    const labels = [
        event.lateMinutes ? `Опоздание · ${event.lateMinutes} мин` : '',
        event.earlyMinutes ? `Ранний уход · ${event.earlyMinutes} мин` : '',
    ].filter(Boolean)
    return labels.length ? labels : ['Нарушение']
}

// Месяцы периода дисциплины в формате query YYYY-MM.
export const monthParam = (month: Dayjs) => month.format('YYYY-MM')

// ---------- Биржа: exchange-summary / vacancy-responses ----------

export type ExchangePeriod = '3m' | 'year' | 'all'
export type ResponseStatus = 'pending' | 'approved' | 'rejected' | 'worked' | 'no_show'

export const RESPONSE_STATUS_LABELS: Record<ResponseStatus, string> = {
    pending: 'Ожидает',
    approved: 'Одобрен',
    rejected: 'Отклонён',
    worked: 'Отработана',
    no_show: 'Неявка',
}
const STATUSES = Object.keys(RESPONSE_STATUS_LABELS) as ResponseStatus[]

export interface ExchangeResponse {
    id: string
    respondedAt: string
    departmentName: string | null
    positionName: string | null
    shiftDate: string | null
    planStart: string | null
    planEnd: string | null
    hours: number | null
    status: ResponseStatus
}

export interface ExchangeSummary {
    workedShifts: number | null
    // Σ shift.duration (план с обедом) — не exchange_hours статистики.
    workedHours: number | null
    responsesCount: number | null
    approvedCount: number | null
    rejectedCount: number | null
    noShowCount: number | null
    departmentsTotal: number | null
    // Контракт отдаёт только число смен: часы по магазину не выводятся.
    topDepartments: { id: string, name: string | null, shifts: number | null }[]
    recentResponses: ExchangeResponse[]
}

function normalizeResponse(item: Record<string, unknown>): ExchangeResponse | null {
    if (typeof item.id !== 'string' || typeof item.responded_at !== 'string' || !STATUSES.includes(item.status as ResponseStatus)) return null
    return {
        id: item.id,
        respondedAt: item.responded_at,
        departmentName: textOrNull(objectOf(item.department).name),
        positionName: textOrNull(objectOf(item.position).name),
        shiftDate: textOrNull(item.shift_date),
        planStart: textOrNull(item.plan_work_start_at),
        planEnd: textOrNull(item.plan_work_end_at),
        hours: numberOrNull(item.hours),
        status: item.status as ResponseStatus,
    }
}

const responsesOf = (value: unknown) => listOf(value).map(normalizeResponse).filter((item): item is ExchangeResponse => item !== null)

export function normalizeExchangeSummary(body: unknown): ExchangeSummary {
    const value = objectOf(body)
    return {
        workedShifts: intOrNull(value.worked_shifts),
        workedHours: numberOrNull(value.worked_hours),
        responsesCount: intOrNull(value.responses_count),
        approvedCount: intOrNull(value.approved_count),
        rejectedCount: intOrNull(value.rejected_count),
        noShowCount: intOrNull(value.no_show_count),
        departmentsTotal: intOrNull(value.departments_total),
        topDepartments: listOf(value.top_departments)
            .filter(item => typeof item.department_id === 'string')
            .map(item => ({ id: item.department_id as string, name: textOrNull(item.name), shifts: intOrNull(item.shifts_count) })),
        recentResponses: responsesOf(value.recent_responses),
    }
}

export function normalizeResponsesPage(body: unknown): Paged<ExchangeResponse> {
    const value = objectOf(body)
    return { count: intOrNull(value.count), results: responsesOf(value.results) }
}

export interface ExchangeRange {
    date_from?: string
    date_to: string
}

// Границы периода датами: 3 месяца — с начала первого из трёх календарных месяцев anchor, год — с 1 января,
// всё время — без date_from. Конец — end (сегодня у активных, конец месяца увольнения у архива).
export function exchangeRange(period: ExchangePeriod, anchor: Dayjs, end: Dayjs): ExchangeRange {
    const to = end.format(DATE_KEY)
    if (period === 'all') return { date_to: to }
    const start = period === '3m' ? anchor.startOf('month').subtract(2, 'month') : anchor.startOf('year')
    return { date_from: start.format(DATE_KEY), date_to: to }
}

export const monthExchangeRange = (month: Dayjs): ExchangeRange => ({
    date_from: month.startOf('month').format(DATE_KEY),
    date_to: month.endOf('month').format(DATE_KEY),
})

// ---------- Кадровые документы: day-state-documents ----------

export interface DayStateDocument {
    id: string
    employmentId: string | null
    dateFrom: string | null
    dateTo: string | null
    dayTypeName: string | null
}

// Не массив — ошибка формата (null), а не пустой список.
export function normalizeDocuments(body: unknown): DayStateDocument[] | null {
    if (!Array.isArray(body)) return null
    return listOf(body)
        .filter(item => typeof item.id === 'string')
        .map(item => ({
            id: item.id as string,
            employmentId: textOrNull(item.employment_id),
            dateFrom: textOrNull(item.date_from),
            dateTo: textOrNull(item.date_to),
            dayTypeName: textOrNull(objectOf(item.day_type).name),
        }))
}

// Виды для «Сейчас и дальше» (решение Романа 27.10.2026): отпуск, больничный, командировка.
// Справочного признака вида у DayType нет (коды настраиваемые) — распознаётся по day_type.name.
const ABSENCE_KINDS = [/отпуск/i, /больничн/i, /командиров/i]
export const isAbsenceKind = (document: DayStateDocument) => Boolean(document.dayTypeName && ABSENCE_KINDS.some(re => re.test(document.dayTypeName!)))

// Календарная дата YYYY-MM-DD: dayjs переносит несуществующую дату (2026-02-31), поэтому format с ней не совпадёт.
const isCalendarDate = (value: string | null): value is string => {
    if (value === null) return false
    const parsed = dayjs(value)
    return parsed.isValid() && parsed.format(DATE_KEY) === value
}

// Документ отображаемого ТУ нужного вида с обеими валидными датами, пересекающий сегодня..+3 месяца:
// текущий, иначе ближайший по дате начала. Нет такого — null (строка не показывается).
export function pickAbsence(documents: DayStateDocument[], employmentId: string, today: Dayjs) {
    const todayKey = today.format(DATE_KEY)
    const limitKey = today.add(3, 'month').format(DATE_KEY)
    const candidates = documents
        .filter(item => item.employmentId === employmentId && isAbsenceKind(item)
            && isCalendarDate(item.dateFrom) && isCalendarDate(item.dateTo)
            && item.dateTo >= todayKey && item.dateFrom <= limitKey)
        .sort((a, b) => a.dateFrom!.localeCompare(b.dateFrom!))
    const current = candidates.find(item => item.dateFrom! <= todayKey)
    if (current) return { document: current, current: true }
    return candidates[0] ? { document: candidates[0], current: false } : null
}
